import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Phone, Instagram, User, ShoppingBag, 
  LogOut, Package, Trash2, Plus, Minus, ArrowRight 
} from 'lucide-react';
import { useSite } from '@/lib/SiteContext';
import ProductImage from '@/components/ProductImage';

const navLinks = [
  { to: '/', label: 'Ana Sayfa' },
  { to: '/urunler', label: 'Ürünler' },
  { to: '/hakkimizda', label: 'Hakkımızda' },
  { to: '/katalog', label: 'Katalog' },
  { to: '/iletisim', label: 'İletişim' },
];

/* ─── SEPET ÇEKMECESİ (CART DRAWER) ─── */
function CartDrawer({ isOpen, onClose }) {
  const { cart, removeFromCart, updateCartQuantity, cartTotal, cartCount } = useSite();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-brand-dark/20 backdrop-blur-sm z-[110]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white z-[111] shadow-2xl flex flex-col"
          >
            <div className="p-8 border-b border-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-extrabold tracking-tighter">Sepetim</h2>
                <span className="bg-brand-pink text-brand-dark text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {cartCount}
                </span>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center hover:bg-slate-100 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 scrollbar-hide">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <ShoppingBag size={48} className="text-slate-100" />
                  <p className="text-slate-400 font-medium">Sepetin henüz boş.</p>
                  <button onClick={onClose} className="text-brand-dark font-bold text-sm underline">Alışverişe Devam Et</button>
                </div>
              ) : (
                <div className="space-y-6">
                  {cart.map((item) => (
                    <motion.div key={item.id + (item.selectedVariantName || '')} layout className="flex gap-4">
                      <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-50 shrink-0 border border-slate-100">
                        <ProductImage src={item.images?.[0]} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-brand-dark text-sm leading-tight">{item.name}</h4>
                            {item.selectedVariantName && <p className="text-[10px] font-bold text-brand-dark/30 uppercase mt-0.5">{item.selectedVariantName}</p>}
                          </div>
                          <button onClick={() => removeFromCart(item.id)} className="text-slate-300 hover:text-rose-500 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3 bg-slate-50 rounded-full px-2 py-1 scale-90 origin-left">
                            <button onClick={() => updateCartQuantity(item.id, -1)} className="text-brand-dark/40 hover:text-brand-dark"><Minus size={14} /></button>
                            <span className="text-xs font-bold">{item.quantity}</span>
                            <button onClick={() => updateCartQuantity(item.id, 1)} className="text-brand-dark/40 hover:text-brand-dark"><Plus size={14} /></button>
                          </div>
                          <span className="font-bold text-brand-dark text-sm">{Number(item.price * item.quantity).toLocaleString('tr-TR')} TL</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-8 border-t border-slate-50 space-y-6">
                <div className="flex justify-between items-end">
                  <span className="text-slate-400 text-xs font-bold uppercase tracking-widest">Toplam</span>
                  <span className="text-2xl font-black tracking-tighter text-brand-dark">{cartTotal.toLocaleString('tr-TR')} TL</span>
                </div>
                <Link to="/odeme" onClick={onClose} className="w-full py-5 bg-brand-dark text-white rounded-full font-bold text-lg flex items-center justify-center gap-3 hover:bg-brand-dark/90 transition-all active:scale-95 shadow-xl shadow-brand-dark/10">
                  Ödemeye Geç <ArrowRight size={20} />
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ─── ANA HEADER BİLEŞENİ ─── */
export default function Header() {
  const [isOpen, setIsOpen] = useState(false); // Mobile Menu
  const [isAuthOpen, setIsAuthOpen] = useState(false); // Login Drawer
  const [isCartOpen, setIsCartOpen] = useState(false); // Cart Drawer
  const [scrolled, setScrolled] = useState(false);
  
  const { user, loginWithGoogle, logout, cartCount } = useSite();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setIsAuthOpen(false);
    setIsCartOpen(false);
  }, [location]);

  return (
    <>
      <header
        className={`fixed top-0 z-[60] w-full transition-all duration-500 ${
          scrolled
            ? 'bg-white/80 backdrop-blur-xl shadow-sm border-b border-slate-100 py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="relative z-50">
              <img
                src="https://customer-assets.emergentagent.com/job_ayz-modern-refresh/artifacts/v7g2xftl_bf9b2368-ab92-47b2-b299-567b89d2d406.jpeg"
                alt="AyzPlastik Logo"
                className="h-12 md:h-14 w-auto transition-transform duration-500 hover:scale-105"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                    location.pathname === link.to
                      ? 'bg-brand-dark text-white'
                      : 'text-brand-dark/60 hover:text-brand-dark hover:bg-slate-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Profile Trigger */}
              <button
                onClick={() => setIsAuthOpen(true)}
                className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-brand-dark hover:bg-brand-dark hover:text-white transition-all duration-300 group overflow-hidden"
              >
                {user?.photoURL ? (
                  <img src={user.photoURL} alt="Profil" className="w-full h-full object-cover" />
                ) : (
                  <User size={20} className="group-hover:scale-110 transition-transform" />
                )}
              </button>

              {/* Cart Trigger */}
              <button 
                onClick={() => setIsCartOpen(true)}
                className="relative w-10 h-10 rounded-full bg-brand-dark text-white flex items-center justify-center hover:bg-brand-dark/90 transition-all duration-300 group"
              >
                <ShoppingBag size={18} className="group-hover:scale-110 transition-transform" />
                {cartCount > 0 && (
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-brand-pink text-brand-dark text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white"
                  >
                    {cartCount}
                  </motion.span>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-brand-dark bg-slate-100"
              >
                {isOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* --- Giriş Çekmecesi --- */}
      <AnimatePresence>
        {isAuthOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsAuthOpen(false)} className="fixed inset-0 bg-brand-dark/20 backdrop-blur-sm z-[100]" />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="fixed right-0 top-0 h-full w-full max-w-[400px] bg-white z-[101] shadow-2xl p-8 flex flex-col">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-2xl font-bold tracking-tighter">{user ? `Selam, ${user.displayName?.split(' ')[0]}` : 'Hesabım'}</h2>
                <button onClick={() => setIsAuthOpen(false)} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center"><X size={20} /></button>
              </div>
              {!user ? (
                <div className="flex-1 flex flex-col justify-center">
                  <button onClick={() => { loginWithGoogle(); setIsAuthOpen(false); }} className="w-full py-4 flex items-center justify-center gap-3 bg-white border-2 border-slate-100 rounded-2xl font-bold hover:bg-slate-50 transition-all active:scale-95 shadow-sm">
                    <img src="https://www.google.com/favicon.ico" className="w-4 h-4" alt="Google" /> Google ile Devam Et
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Link to="/siparisler" className="flex items-center gap-4 p-5 rounded-2xl bg-slate-50 hover:bg-brand-pink/10 transition-colors group">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-brand-dark shadow-sm"><Package size={24} /></div>
                    <div><h4 className="font-bold">Siparişlerim</h4><p className="text-xs text-slate-400">Takip et</p></div>
                  </Link>
                  <button onClick={() => { logout(); setIsAuthOpen(false); }} className="w-full flex items-center gap-4 p-5 rounded-2xl hover:bg-rose-50 text-rose-500 transition-colors mt-8">
                    <LogOut size={20} /> <span className="font-bold">Güvenli Çıkış</span>
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* --- Sepet Çekmecesi --- */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* --- Mobil Menü --- */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-brand-dark/20 backdrop-blur-sm z-40 lg:hidden" />
            <motion.nav initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="fixed left-0 top-0 h-full w-[80%] bg-white z-50 p-8 lg:hidden flex flex-col">
              <div className="flex items-center justify-between mb-12">
                <img src="https://customer-assets.emergentagent.com/job_ayz-modern-refresh/artifacts/v7g2xftl_bf9b2368-ab92-47b2-b299-567b89d2d406.jpeg" className="h-10 w-auto" alt="Logo" />
                <button onClick={() => setIsOpen(false)} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center"><X size={20} /></button>
              </div>
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link key={link.to} to={link.to} className={`px-6 py-4 rounded-2xl text-lg font-bold ${location.pathname === link.to ? 'bg-brand-dark text-white' : 'text-brand-dark/60'}`}>{link.label}</Link>
                ))}
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}